import React from 'react';
import clsx from 'clsx';
import I18n from 'i18n-js';
import moment from 'moment';
import { truncate } from '@/modules/TCIUtils';
import assessmentIcon from 'images/icons/assessment-white-outline.svg';
import bookIcon from 'images/icons/open-books-white.svg';
import classActivityIcon from 'images/icons/hand.svg';
import gameIcon from 'images/icons/controller-white-outline.svg';
import investigationIcon from 'images/icons/investigation-white-outline.svg';
import clipboardIcon from 'images/icons/clipboard-white-outline.svg';
import videoActivityIcon from 'images/icons/video-activity-white-outline.svg';
import { libraryIconFor } from '@/components/shared/LessonOverview/Cards/icons/libraryIcons';
import { svgAsImgSrc } from '@/components/common/Utils';
import Pluralize from 'pluralize';
import CoTeacherBadge from '@/components/shared/CoTeacherBadge';
import Tooltip from '../../../../common/Tooltip';
import styles from './AssignmentCards.module.scss';
import { AssignmentModelsHashType, AssignmentType, UserType } from '../../types';
import { ICON_MAPPING } from '../../../NewUnitOverview/Cards/UnitSlideShowCard/Icon';

interface AssignmentCardProps {
  assignment: AssignmentType;
  assignmentModelsHash: AssignmentModelsHashType;
  coTeacherVisibility: boolean;
  isOverdue?: boolean;
  user: UserType;
}

const ReferenceMaterialIcon = ({ tag }: { tag?: string | null }) => {
  const { Icon } = libraryIconFor(tag);

  return <Icon color="white" />;
};

const AssignmentCard = ({
  assignment, assignmentModelsHash, coTeacherVisibility, isOverdue, user,
}: AssignmentCardProps) => {
  if (!assignment || !assignmentModelsHash) return <></>;

  const assignmentTypes = Object.keys(assignmentModelsHash);
  const sectionDetails = (assignmentModelsHash.Section || []).filter(section => !section.stream);
  const allAssignmentDetails = assignmentTypes.flatMap((type) => {
    const items = assignmentModelsHash[type] || [];

    // Only use notebook and reading game items if the reading text has not been assigned
    if (sectionDetails.length === 0) {
      return items;
    } if (type === 'Notebook') {
      return items.filter(notebook => (
        !notebook.unit_project
          && !notebook.video_activity
          && !sectionDetails.find(assignedReading => assignedReading.id === notebook.parent_section)
      ));
    } if (type === 'ReadingGame') {
      return items.filter(readingGame => !sectionDetails.find(
        assignedReading => assignedReading.id === readingGame.parent_section
      ));
    }

    return items;
  }) || [];

  const firstAssignmentDetails = allAssignmentDetails[0];
  const hasSingleAssignment = allAssignmentDetails.length === 1;
  const availableToStart = new Date(assignment.start_datetime) < new Date() || user.type !== 'Student';

  const getIcon = (): { icon: string; iconColorClass: string } => {
    switch (firstAssignmentDetails.assignment_type) {
      case 'Assessment':
        return { icon: assessmentIcon, iconColorClass: 'tw-bg-orange' };
      case 'Challenge': case 'Game':
        return { icon: gameIcon, iconColorClass: 'tw-bg-orange' };
      case 'ReferenceMaterial':
        return {
          icon: svgAsImgSrc(<ReferenceMaterialIcon tag={firstAssignmentDetails.assignable_label} />),
          iconColorClass: 'tw-bg-teal-blue',
        };
      case 'Section': case 'Notebook': case 'ReadingGame':
        return {
          icon: firstAssignmentDetails.stream ? investigationIcon
            : firstAssignmentDetails.unit_project ? clipboardIcon
              : bookIcon,
          iconColorClass: 'tw-bg-tci-purple',
        };
      case 'SlideShow': case 'SlideGroup':
        switch (firstAssignmentDetails.activity_type) {
          case 'classroom_activity':
            return { icon: classActivityIcon, iconColorClass: 'tw-bg-green' };
          case 'investigation_activity':
            return { icon: investigationIcon, iconColorClass: 'tw-bg-green' };
          case 'video_activity':
            return { icon: videoActivityIcon, iconColorClass: 'tw-bg-green' };
          case 'reading_activity':
            return { icon: bookIcon, iconColorClass: 'tw-bg-green' };
          case 'lesson_outline':
            return { icon: clipboardIcon, iconColorClass: 'tw-bg-green' };
          default:
            return { icon: bookIcon, iconColorClass: 'tw-bg-green' };
        }
      default:
        return { icon: bookIcon, iconColorClass: 'tw-bg-tci-purple' };
    }
  };

  const getDueDate = () => {
    const dueToday = moment(assignment.due_datetime).startOf('day').isSame(moment().startOf('day'));
    const dueTomorrow = moment(assignment.due_datetime).startOf('day').isSame(moment().add(1, 'day').startOf('day'));

    const label = dueToday ? I18n.t('today')
      : dueTomorrow ? I18n.t('tomorrow')
        : moment(assignment.due_datetime).format('ddd MMM D');

    return (
      <div
        className={clsx('tw-flex tw-items-center tw-text-base tw-whitespace-nowrap', {
          'tw-text-green': dueToday,
          'tw-text-red': isOverdue,
        })}
      >
        <span className="fa fa-clock-o tw-pr-2 tw-text-xl" /> {I18n.t('due')} {label}
      </div>
    );
  };

  const renderDirections = notes => (
    <div>
      <span className="tw-font-bold tw-pr-1">Directions: </span>
      <span>{notes}</span>
    </div>
  );

  const renderDirectionsWithTooltip = () => {
    if (!assignment.notes || assignment.notes.length === 0) return null;

    const maxLength = 150;

    return (
      assignment.notes.length >= maxLength ? (
        <Tooltip content={renderDirections(assignment.notes)} placement="bottom">
          <span>{renderDirections(truncate(assignment.notes, maxLength))}</span>
        </Tooltip>
      ) : renderDirections(assignment.notes)
    );
  };

  const UnitIcon = ICON_MAPPING[firstAssignmentDetails.unit_type];
  const { iconColorClass, icon } = getIcon();

  const renderCardContent = () => (
    <>
      <div className={clsx(iconColorClass, styles.cardImage)}>
        {UnitIcon ? (
          <UnitIcon color="white" width="65%" />
        ) : (
          <img alt={firstAssignmentDetails.assignable_type || assignmentTypes[0]} src={icon} />
        )}
      </div>
      <div className={styles.cardDetails}>
        <div className="tw-text-[#3C3C3C] tw-text-base tw-pb-1">
          <span className="tw-font-bold">
            {assignment.assignment_title}
            {` (${allAssignmentDetails.length} ${Pluralize('Item', allAssignmentDetails.length)})`}
            <span className="tw-mx-2 tw-text-gray-500">|</span>
            {assignment.assignment_unit_lesson}
          </span>
          {assignment.assignment_unit_lesson ? ' - ' : ''}
          <span>
            {assignment.assignment_location_title}
          </span>
        </div>
        {/* If the assignment doesn't belong to the current user, show assignment's creator */}
        {coTeacherVisibility && user.id !== assignment.staffer_id && (
          <CoTeacherBadge stafferId={assignment.staffer_id} teacherDisplayName={assignment.teacher_display_name} />
        )}
        <ul className="tw-leading-5 tw-list-none tw-m-0 tw-p-0">
          {allAssignmentDetails.map((assignmentDetails, index) => {
            if (!availableToStart) {
              return (
                <li
                  key={`assignment-link-${index}`}
                  className="tw-flex tw-items-center"
                >
                  <div dangerouslySetInnerHTML={{ __html: assignmentDetails.label }} />
                  <Tooltip content={I18n.t('assignment_not_started')}>
                    <i className="fa fa-icon fa-info-circle tw-ml-1.5" />
                  </Tooltip>
                </li>
              );
            }

            // AIDEV-NOTE: single-item cards link from the card wrapper; a nested <a> here violates WCAG 1.3.1
            if (hasSingleAssignment) {
              return (
                <li
                  key={`assignment-link-${index}`}
                  className="hover:tw-underline tw-flex tw-items-center"
                >
                  <div dangerouslySetInnerHTML={{ __html: assignmentDetails.label }} />
                  <i className="fa fa-external-link tw-no-decoration tw-pl-2" />
                </li>
              );
            }

            return (
              <li key={`assignment-link-${index}`}>
                <a
                  className="hover:tw-underline tw-flex tw-items-center"
                  href={assignmentDetails.href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <div dangerouslySetInnerHTML={{ __html: assignmentDetails.label }} />
                  <i className="fa fa-external-link tw-no-decoration tw-pl-2" />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      <div className={`${styles.assignmentDirections} tw-text-[#3C3C3C]`}>
        <div className="tw-flex tw-justify-end tw-mb-5">{getDueDate()}</div>
        {renderDirectionsWithTooltip()}
      </div>
    </>
  );

  return availableToStart && hasSingleAssignment ? (
    <a
      className={styles.cardInfo}
      href={allAssignmentDetails[0].href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {renderCardContent()}
    </a>
  ) : (
    <div className={styles.cardInfo}>
      {renderCardContent()}
    </div>
  );
};

export default AssignmentCard;
